/**
 * Step 11 — Material & Contents Inventory.
 */
import { useState } from "react";
import API from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Card, CardContent } from "@/components/ui/card";
import { Boxes, Plus, Trash2, Loader2, Check, AlertCircle, X } from "lucide-react";
import { toast } from "sonner";

const CONDITIONS = ["Damaged", "Destroyed", "Salvageable"];

export default function Step11Inventory({ workflow, requestId, onAdvance, fetchWorkflow }) {
  const init = workflow?.step_11?.items || [];
  const [items, setItems] = useState(init);
  const [saving, setSaving] = useState(false);
  const [showNa, setShowNa] = useState(false);
  const [naReason, setNaReason] = useState("");

  const add = () => setItems(prev => [...prev, {
    name: "", location: "", condition: "Damaged", estimated_value: 0, notes: "",
  }]);
  const update = (i, patch) => setItems(prev => prev.map((x, idx) => idx === i ? { ...x, ...patch } : x));
  const remove = (i) => setItems(prev => prev.filter((_, idx) => idx !== i));

  const save = async () => {
    if (items.length === 0) return toast.error("Add at least one item, or use Not Applicable");
    for (const it of items) if (!it.name.trim() || !it.location.trim())
      return toast.error("Every item needs a name and location");
    setSaving(true);
    try {
      await API.post(`/inspection/${requestId}/step/11/save`, { items });
      toast.success("Inventory saved");
      onAdvance(12);
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not save"); }
    finally { setSaving(false); }
  };

  const markNa = async () => {
    if (!naReason.trim()) return toast.error("Reason required");
    try {
      await API.put(`/inspection/${requestId}/step/11/status`, { status: "na", na_reason: naReason.trim() });
      toast.success("Step 11 marked Not Applicable");
      setShowNa(false); fetchWorkflow(); onAdvance(12);
    } catch (err) { toast.error("Could not mark N/A"); }
  };

  return (
    <div className="space-y-4" data-testid="step-11-form">
      <div className="flex items-center gap-2">
        <Boxes className="w-5 h-5 text-amber-600" />
        <h2 className="text-base font-bold text-zinc-900">Step 11 · Material & Contents Inventory</h2>
      </div>

      {items.length === 0 && (
        <Card><CardContent className="pt-6 pb-6 text-center">
          <Boxes className="w-8 h-8 text-zinc-300 mx-auto mb-1" />
          <p className="text-xs text-zinc-500">No items yet. Add damaged contents and materials below.</p>
        </CardContent></Card>
      )}

      {items.map((it, i) => (
        <Card key={i} data-testid={`step-11-row-${i}`}>
          <CardContent className="pt-3 pb-3 space-y-2">
            <div className="flex items-center gap-2">
              <Input value={it.name} onChange={e => update(i, { name: e.target.value })} placeholder="Item name"
                     className="h-10 text-sm font-semibold flex-1" data-testid={`step-11-name-${i}`} />
              <Button size="sm" variant="ghost" onClick={() => remove(i)} className="text-rose-500" data-testid={`step-11-remove-${i}`}>
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            </div>
            <Input value={it.location} onChange={e => update(i, { location: e.target.value })} placeholder="Location (room)"
                   className="h-10 text-xs" data-testid={`step-11-loc-${i}`} />
            <div className="grid grid-cols-2 gap-2">
              <Select value={it.condition} onValueChange={v => update(i, { condition: v })}>
                <SelectTrigger className="h-10 text-xs" data-testid={`step-11-cond-${i}`}><SelectValue /></SelectTrigger>
                <SelectContent>{CONDITIONS.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
              <Input type="number" value={it.estimated_value} onChange={e => update(i, { estimated_value: parseFloat(e.target.value) || 0 })}
                     placeholder="Estimated value ($)" className="h-10 text-xs" data-testid={`step-11-value-${i}`} />
            </div>
            <Input value={it.notes || ""} onChange={e => update(i, { notes: e.target.value })} placeholder="Notes"
                   className="h-10 text-xs" />
          </CardContent>
        </Card>
      ))}

      <Button variant="outline" onClick={add} className="w-full h-11" data-testid="step-11-add-item">
        <Plus className="w-4 h-4 mr-1" /> Add Item
      </Button>

      <div className="grid grid-cols-2 gap-2">
        <Button variant="outline" onClick={() => setShowNa(true)} className="h-12" data-testid="step-11-na-btn">
          <AlertCircle className="w-4 h-4 mr-1" /> Not Applicable
        </Button>
        <Button onClick={save} disabled={saving} className="h-12 bg-emerald-600 text-white" data-testid="step-11-save-btn">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Check className="w-4 h-4 mr-1" /> Save & Continue</>}
        </Button>
      </div>

      {showNa && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-end sm:items-center justify-center p-4" data-testid="step-11-na-dialog">
          <Card className="w-full max-w-sm"><CardContent className="pt-4 space-y-3">
            <div className="flex justify-between"><h3 className="text-sm font-bold">Mark Not Applicable</h3>
              <Button size="sm" variant="ghost" onClick={() => setShowNa(false)}><X className="w-4 h-4" /></Button></div>
            <textarea value={naReason} onChange={e => setNaReason(e.target.value)} placeholder="Reason (required)"
                      className="w-full p-2 text-xs border rounded h-20" data-testid="step-11-na-reason" />
            <Button onClick={markNa} className="w-full h-11 bg-zinc-900 text-white" data-testid="step-11-na-submit">Confirm</Button>
          </CardContent></Card>
        </div>
      )}
    </div>
  );
}
